import moment from "moment";

const VENDORS = [
    { key: "PINELABS", label: "PineLabs" },
    { key: "RAZORPAY", label: "Razorpay" },
    { key: "VOUCHAGRAM", label: "VouchaGram" },
];

const VENDOR_FIELDS = [
    { key: "sku", label: "SKU" },
    { key: "type", label: "Type" },
    { key: "discount", label: "Discount" },
    { key: "cashback", label: "Cashback" },
    { key: "min", label: "Min" },
    { key: "max", label: "Max" },
    { key: "denominations", label: "Denominations" },
    { key: "is_active", label: "Active" },
    { key: "id", label: "ID" },
    { key: "created_at", label: "Created" },
    { key: "updated_at", label: "Updated" },
];

const TOP_LEVEL_COLUMNS = [
    { label: "Name", get: (sb) => sb.name },
    { label: "Code", get: (sb) => sb.code },
    {
        label: "Parent Brand",
        get: (sb) => {
            const list = Array.isArray(sb.brands) ? sb.brands : (sb.brand ? [sb.brand] : []);
            return list.map((b) => b?.name).filter(Boolean).join(", ");
        },
    },
    { label: "Description", get: (sb) => sb.description },
    { label: "Redirect Link", get: (sb) => sb.redirect_link },
    { label: "BG", get: (sb) => sb.hex_code_bg },
    { label: "Text", get: (sb) => sb.hex_code_text },
    { label: "Active", get: (sb) => boolToYesNo(sb.is_active) },
    { label: "Wallet", get: (sb) => boolToYesNo(sb.is_wallet) },
    { label: "PIN", get: (sb) => boolToYesNo(sb.is_pin) },
    { label: "Excl. Referral", get: (sb) => boolToYesNo(sb.excluded?.is_referral) },
    { label: "Excl. Active", get: (sb) => boolToYesNo(sb.excluded?.is_activation_promotion) },
    {
        label: "How it Works",
        get: (sb) => Array.isArray(sb.how_it_works)
            ? sb.how_it_works.map((it, i) => `${i + 1}. ${it}`).join(" | ")
            : "",
    },
    {
        label: "Terms & Conditions",
        get: (sb) => Array.isArray(sb.terms_and_conditions)
            ? sb.terms_and_conditions.map((it, i) => `${i + 1}. ${it}`).join(" | ")
            : "",
    },
    { label: "Clubbable", get: (sb) => boolToYesNo(extractEnabled(sb.dynamic_details?.is_clubbable)) },
    { label: "Balance Reusable", get: (sb) => boolToYesNo(extractEnabled(sb.dynamic_details?.is_balance_reusable)) },
    { label: "Card Info", get: (sb) => boolToYesNo(extractEnabled(sb.dynamic_details?.card_info)) },
    { label: "ID", get: (sb) => sb.id },
    { label: "Created", get: (sb) => formatDate(sb.created_at) },
    { label: "Updated", get: (sb) => formatDate(sb.updated_at) },
];

const VENDOR_COLUMNS = VENDORS.flatMap(({ key, label }) =>
    VENDOR_FIELDS.map((f) => ({
        label: `${label} · ${f.label}`,
        get: (sb) => {
            const v = findVendor(sb.sub_brand_vendors, key);
            if (!v) return "";
            return formatVendorField(f.key, v[f.key]);
        },
    }))
);

const COLUMNS = [...TOP_LEVEL_COLUMNS, ...VENDOR_COLUMNS];

function boolToYesNo(value) {
    if (value == null) return "";
    return value ? "YES" : "NO";
}

function extractEnabled(value) {
    if (value == null) return null;
    if (typeof value === "boolean") return value;
    if (typeof value === "object") return value.is_enabled ?? null;
    return null;
}

function formatDate(value) {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM YYYY, HH:mm") : value;
}

function findVendor(vendors, vendorKey) {
    if (!Array.isArray(vendors)) return null;
    const target = vendorKey.toUpperCase();
    return vendors.find((v) => (v?.name || "").toUpperCase() === target) || null;
}

function formatVendorField(field, value) {
    if (value == null) return "";
    if (field === "is_active") return boolToYesNo(value);
    if (field === "denominations") return Array.isArray(value) ? value.join(", ") : "";
    if (field === "created_at" || field === "updated_at") return formatDate(value);
    return String(value);
}

function escapeCell(value) {
    const s = value == null ? "" : String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

export const buildSubBrandsCSV = (subBrands) => {
    const header = COLUMNS.map((c) => escapeCell(c.label)).join(",");
    const rows = (subBrands || []).map((sb) =>
        COLUMNS.map((c) => escapeCell(c.get(sb))).join(",")
    );
    return [header, ...rows].join("\n");
};

export const downloadCSV = (filename, csv) => {
    // Prepend BOM so Excel auto-detects UTF-8.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    // Defer cleanup; some browsers cancel the download if the URL is revoked
    // before the click is fully handled.
    setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, 200);
};
